import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, ArrowUpRight, Cookie } from "lucide-react";
import { LINKS, ASSETS, BRAND } from "../../lib/constants";
// Cookiebot exposes window.Cookiebot.renew() to reopen the consent banner
const openCookieBanner = () => {
  if (typeof window !== "undefined" && window.Cookiebot && typeof window.Cookiebot.renew === "function") {
    window.Cookiebot.renew();
  }
};

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-brand-black text-white">
      <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 gap-x-14">
          {/* Col 1 — Logo + socials */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <img
                src={ASSETS.logo}
                alt="ProfitMIND"
                className="block h-[88px] w-auto rounded-2xl"
              />
            </Link>
            <div className="flex items-center gap-3 mt-6">
              <a href={LINKS.linkedinPersonal} target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin" aria-label="LinkedIn" className="p-2.5 rounded-md border border-white/30 hover:bg-brand-yellow hover:border-brand-yellow hover:text-brand-black transition-colors">
                <Linkedin size={16} />
              </a>
              <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" aria-label="Instagram" className="p-2.5 rounded-md border border-white/30 hover:bg-brand-yellow hover:border-brand-yellow hover:text-brand-black transition-colors">
                <Instagram size={16} />
              </a>
              <a href={`mailto:${LINKS.email}`} data-testid="footer-social-email" aria-label="Email" className="p-2.5 rounded-md border border-white/30 hover:bg-brand-yellow hover:border-brand-yellow hover:text-brand-black transition-colors">
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Col 2 — Explore */}
          <div className="md:col-span-2">
            <h4 className="font-display text-lg uppercase mb-5 text-brand-yellow">Explore</h4>
            <ul className="space-y-3 text-[15px]">
              <li><Link to="/" data-testid="footer-nav-home" className="text-white/90 hover:text-brand-yellow transition-colors">Home</Link></li>
              <li><Link to="/about" data-testid="footer-nav-about" className="text-white/90 hover:text-brand-yellow transition-colors">About</Link></li>
              <li><Link to="/work-with-me" data-testid="footer-nav-work-with-me" className="text-white/90 hover:text-brand-yellow transition-colors">Work With Me</Link></li>
              <li><Link to="/follow-the-cash" data-testid="footer-nav-follow-the-cash" className="text-white/90 hover:text-brand-yellow transition-colors">Follow The Cash™</Link></li>
              <li><Link to="/contact" data-testid="footer-nav-contact" className="text-white/90 hover:text-brand-yellow transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Col 3 — Quick links (no header, aligned with col 2 rows) */}
          <div className="md:col-span-2">
            <h4 className="font-display text-lg uppercase mb-5 text-brand-yellow opacity-0 select-none" aria-hidden="true">.</h4>
            <ul className="space-y-3 text-[15px]">
              <li>
                <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" data-testid="footer-nav-free-reality-check" className="text-white/90 hover:text-brand-yellow transition-colors inline-flex items-center gap-1">
                  Free Reality Check <ArrowUpRight size={12} />
                </a>
              </li>
              <li>
                <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" data-testid="footer-link-forecastly" className="text-white/90 hover:text-brand-yellow transition-colors inline-flex items-center gap-1">
                  forecastly <ArrowUpRight size={12} />
                </a>
              </li>
              <li>
                <a href={LINKS.tidycal} target="_blank" rel="noopener noreferrer" data-testid="footer-link-book" className="text-white/90 hover:text-brand-yellow transition-colors inline-flex items-center gap-1">
                  Book a call <ArrowUpRight size={12} />
                </a>
              </li>
            </ul>
          </div>

          {/* Col 4 — Legal */}
          <div className="md:col-span-3">
            <h4 className="font-display text-lg uppercase mb-5 text-brand-yellow">Legal</h4>
            <ul className="space-y-3 text-[15px]">
              <li><Link to="/privacy-policy" data-testid="footer-link-privacy" className="text-white/90 hover:text-brand-yellow transition-colors">Privacy Policy</Link></li>
              <li><Link to="/cookie-policy" data-testid="footer-link-cookies" className="text-white/90 hover:text-brand-yellow transition-colors">Cookie Policy</Link></li>
              <li><Link to="/terms-of-use" data-testid="footer-link-terms-use" className="text-white/90 hover:text-brand-yellow transition-colors">Terms of Use</Link></li>
              <li><Link to="/terms-and-conditions" data-testid="footer-link-terms-conditions" className="text-white/90 hover:text-brand-yellow transition-colors">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/15 mt-12 pt-6 flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4 text-xs text-white/70">
          <p>© {year} {BRAND.parent}. All rights reserved. Registered in England &amp; Wales. Reg. no. 09918255</p>
          <div className="flex flex-col gap-2 items-start sm:items-end">
            <p className="uppercase tracking-[0.14em] text-brand-yellow font-bold sm:text-right">Profitable But Broke · Follow The Cash™ · Own Your Future</p>
            <button
              type="button"
              onClick={openCookieBanner}
              data-testid="manage-cookies-btn"
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow transition-colors"
            >
              <Cookie size={13} /> Manage cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
